import { CameraIcon } from '@radix-ui/react-icons';
import { ParseKeys } from 'i18next';
import { Fragment } from 'react';
import cn from '../../../app/utils/cn';
import Divider from '../Divider';
import DropdownActions from '../DropdownActions.tsx';
import MobileSuperiorHeader from '../MobileSuperiorHeader';
import NavItem from '../NavItem';
import { SidebarHeader } from '../SidebarHeader/index.tsx';
import { useSidebarController } from './useSidebarController';

export interface INavItems {
  icon: React.ReactElement;
  description: string;
  pathToNavigate: string;
  danger?: boolean;
  onClick?: () => void;
}

export interface INavGroup {
  labelKey?: ParseKeys;
  items: INavItems[];
}

export default function Sidebar() {
  const {
    isOpen,
    isMobile,
    navAnimation,
    groups,
    footerItems,
    AnimatedNav,
    user,
    toggleSidebar,
    navigateTo,
    tenantSignIn,
    t,
  } = useSidebarController();
  return (
    <>
      <MobileSuperiorHeader />

      {isMobile && isOpen && (
        <button
          type="button"
          aria-label={t('sidenavbar.actions.closeMenu')}
          className="absolute inset-0 z-4 bg-black/40 backdrop-blur-[1px] md:hidden"
          onClick={() => toggleSidebar(false)}
        />
      )}

      <AnimatedNav
        className={cn(
          'bg-surface-sidebar absolute z-5 flex h-full flex-col px-3 py-2 md:relative md:rounded-r-xl',
          isOpen && 'w-48',
          isMobile && !isOpen && 'pointer-events-none overflow-hidden',
        )}
        style={navAnimation}
        aria-hidden={isMobile && !isOpen}
      >
        {user && user.memberships.length > 1 && (
          <DropdownActions
            title={t('sidenavbar.header.organizations')}
            options={user.memberships.map(({ tenant }) => ({
              description: tenant.name,
              disabled: !tenant.active,
              className: 'text-grey-500',
              icon: <CameraIcon className="size-4" />,
              selected: tenant.id === user.selectedTenantId,
              onClick: () => tenantSignIn(tenant),
            }))}
          >
            <SidebarHeader />
          </DropdownActions>
        )}

        {user && user.memberships.length === 1 && (
          <SidebarHeader></SidebarHeader>
        )}

        <ul className="mt-4 flex grow flex-col gap-2 overflow-x-hidden overflow-y-auto">
          {groups.map((group, groupIndex) => (
            <Fragment key={group.labelKey ?? `group-${groupIndex}`}>
              {groupIndex > 0 && (
                <li className="mt-1 list-none">
                  <Divider className="border-grey-0 dark:border-grey-600 border-dashed" />
                  {isOpen && group.labelKey && (
                    <p className="text-text-tertiary mt-2 px-2 text-xs tracking-wide uppercase">
                      {t(group.labelKey)}
                    </p>
                  )}
                </li>
              )}

              {group.items.map((item) => (
                <NavItem
                  key={item.description}
                  description={item.description}
                  navigateTo={item.pathToNavigate}
                  onClick={() => {
                    navigateTo(item.pathToNavigate);
                  }}
                  color={item.danger ? 'danger' : 'primary'}
                >
                  <div>{item.icon}</div>
                </NavItem>
              ))}
            </Fragment>
          ))}
        </ul>

        <Divider className="border-grey-0 dark:border-grey-600 my-2 border-dashed"></Divider>

        <ul className="mt-4 flex flex-col gap-1">
          {footerItems?.map((item) => (
            <NavItem
              key={item.description}
              description={item.description}
              navigateTo={item.pathToNavigate}
              color={item.danger ? 'danger' : 'primary'}
              onClick={item.onClick}
            >
              <div>{item.icon}</div>
            </NavItem>
          ))}
        </ul>
      </AnimatedNav>
    </>
  );
}
